import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, User } from 'lucide-react';
import ConsistencyLogo from '@/components/ConsistencyLogo';
import { useLegalLinks, getLegalUrl } from '@/hooks/useLegalLinks';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Signup = () => {
  const { checkAuth } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { documents: signupLinks } = useLegalLinks('signup');

  // Pricing-first enforcement: Redirect if no plan selected
  React.useEffect(() => {
    const selectedPlan = localStorage.getItem('selected_plan');
    if (!selectedPlan) {
      navigate('/pricing', { replace: true });
    }
  }, [navigate]);

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(
        `${API}/auth/signup`,
        { name, email, password },
        { withCredentials: true }
      );
      await checkAuth();
      toast.success('Account created successfully!');
      
      // Check if user came from pricing page
      const selectedPlan = localStorage.getItem('selected_plan');
      if (selectedPlan) {
        // Pricing-first flow: redirect to onboarding choose-plan
        navigate('/onboarding/choose-plan');
      } else {
        // Direct signup: go to welcome
        navigate('/onboarding/welcome');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    const redirectUrl = `${window.location.origin}/auth/google/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#121417] p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8 flex flex-col items-center">
          <ConsistencyLogo variant="full" size="lg" className="mb-4" />
          <p className="text-[#8A93A6] text-lg">Create your account and get started.</p>
        </div>

        {/* Signup Card */}
        <div className="glass-strong rounded-2xl p-8 space-y-6">
          <form onSubmit={handleSignup} className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-[#8A93A6] mb-2">Full Name</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A93A6]" size={18} />
                <input
                  data-testid="signup-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-field pl-12"
                  placeholder="John Doe"
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-[#8A93A6] mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A93A6]" size={18} />
                <input
                  data-testid="signup-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field pl-12"
                  placeholder="you@example.com"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-[#8A93A6] mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A93A6]" size={18} />
                <input
                  data-testid="signup-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field pl-12 pr-12"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8A93A6] hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              data-testid="signup-submit-button"
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#0F1A2E] text-[#8A93A6]">Or continue with</span>
            </div>
          </div>

          {/* Google Signup */}
          <button
            data-testid="google-signup-button"
            onClick={handleGoogleSignup}
            className="btn-secondary w-full flex items-center justify-center gap-3"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M19.6 10.227c0-.709-.064-1.39-.182-2.045H10v3.868h5.382a4.6 4.6 0 01-1.996 3.018v2.51h3.232c1.891-1.742 2.982-4.305 2.982-7.35z" fill="#4285F4"/>
              <path d="M10 20c2.7 0 4.964-.895 6.618-2.423l-3.232-2.509c-.895.6-2.04.955-3.386.955-2.605 0-4.81-1.76-5.595-4.123H1.064v2.59A9.996 9.996 0 0010 20z" fill="#34A853"/>
              <path d="M4.405 11.9c-.2-.6-.314-1.24-.314-1.9 0-.66.114-1.3.314-1.9V5.51H1.064A9.996 9.996 0 000 10c0 1.614.386 3.14 1.064 4.49L4.405 11.9z" fill="#FBBC05"/>
              <path d="M10 3.977c1.468 0 2.786.505 3.823 1.496l2.868-2.868C14.959.99 12.695 0 10 0 6.09 0 2.71 2.24 1.064 5.51l3.34 2.59C5.19 5.737 7.395 3.977 10 3.977z" fill="#EA4335"/>
            </svg>
            Sign up with Google
          </button>
        </div>

        {/* Login link */}
        <p className="text-center mt-6 text-[#8A93A6]">
          Already have an account?{' '}
          <Link to="/login" className="text-[#3A7BFF] hover:text-[#39E6C8] transition-colors font-medium">
            Sign in
          </Link>
        </p>

        {/* Legal links */}
        {signupLinks.length > 0 && (
          <p className="text-center mt-4 text-xs text-[#8A93A6]">
            By creating an account, you agree to our{' '}
            {signupLinks.map((doc, idx) => (
              <span key={doc.slug}>
                <Link 
                  to={getLegalUrl(doc.slug)} 
                  className="text-[#3A7BFF] hover:text-[#39E6C8] transition-colors"
                  target="_blank"
                >
                  {doc.title}
                </Link>
                {idx < signupLinks.length - 1 && ' and '}
              </span>
            ))}
          </p>
        )}
      </div>
    </div>
  );
};

export default Signup;
